// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyCustomer, TyCustomerRef, TyLead, TyProject, TyTeamMember, TyUnit } from "@/types";
import { readLangValue } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import ChoiceInput from "@/UI/inputs/ChoiceInput";
import MultiSelectInput from "@/UI/inputs/MultiSelectInput";
import PhoneInput from "@/UI/inputs/PhoneInput";
import RangeInput from "@/UI/inputs/RangeInput";
import SelectInput from "@/UI/inputs/SelectInput";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TyLeadFormDetails = {
    /** The deal being edited, or `null` to open one. */
    lead?: TyLead | null;
    /**
     * Opened from a person's own screen, so the person is already known. Their name and phone are
     * filled in and locked — the API matches on the phone, and letting it be edited here would
     * quietly file the enquiry against somebody else.
     */
    customer?: TyCustomer | null;
    /**
     * Opened from a project's own interests tab, so the development is already decided. It only
     * seeds the field — it stays editable, because filing an enquiry against the wrong project is
     * the correction somebody makes in this same form a minute later.
     */
    projectId?: string | null;
    /** Opened from a unit's own interests tab. Its project rides along, since a unit has only one. */
    unitId?: string | null;
    onDone: () => void;
};

type Props = { onClose: () => void; details?: unknown };

/**
 * A deal. Opening one takes a person with it, which is why this form asks for a name and a phone
 * rather than making somebody create the customer first: the API matches them by phone and files the
 * lead against whoever is already there. A repeat buyer never becomes two people.
 *
 * **Stage is not on this form.** It has its own control, with its own rules — a member moves forward
 * only, a loss needs a reason — and a field sitting quietly in an edit form would obey none of them.
 *
 * **Neither is a first contact.** A deal typed at the desk has not been talked to yet — the name came
 * off a card or out of somebody's head — so there is nothing that happened to write down, and the
 * row this form used to offer was a call on the timeline that never took place. The catalog is the
 * exception and files its own, because an enquiry *is* a contact.
 *
 * Editing does not show the person: the customer is edited on their own screen.
 */
export default function LeadFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";
    const { lead, customer, projectId, unitId, onDone } = details as TyLeadFormDetails;

    const person = typeof lead?.customer === "object" ? (lead.customer as TyCustomerRef) : (customer ?? null);
    const isEditing = !!lead;
    /** Known up front: the fields are shown filled and dead rather than hidden, so it is obvious whose. */
    const isKnownPerson = !isEditing && !!customer;

    const [name, setName] = useState(person?.name ?? "");
    const [phone, setPhone] = useState<string | undefined>(person?.phone ?? "");
    const [email, setEmail] = useState(person?.email ?? "");
    const [project, setProject] = useState(typeof lead?.project === "object" && lead.project ? lead.project._id : (projectId ?? ""));
    const [unit, setUnit] = useState(typeof lead?.unit === "object" && lead.unit ? lead.unit._id : (unitId ?? ""));
    const [source, setSource] = useState<string>(lead?.source ?? "manual");
    const [financing, setFinancing] = useState<string>(lead?.financing ?? "");
    const [budgetMin, setBudgetMin] = useState<number | null>(lead?.budgetMin ?? null);
    const [budgetMax, setBudgetMax] = useState<number | null>(lead?.budgetMax ?? null);
    const [holders, setHolders] = useState<string[]>(
        lead?.assignees.map((entry) => (typeof entry.client === "object" ? entry.client._id : String(entry.client))) ?? []
    );

    const projects = useFetch<{ projects: TyProject[] }>("/properties/projects", { query: { limit: constants.maxPageSize } });
    const team = useFetch<{ members: TyTeamMember[] }>("/team");
    const units = useFetch<{ units: TyUnit[] }>("/properties/units", {}, false);

    const path = isEditing ? `/crm/leads/${lead._id}` : "/crm/leads";
    const { loading, error, succeededAt, invoke } = useFetch(path, {}, false);

    // The unit list is the chosen project's. Asking for every unit in the company would be a list
    // nobody can read, and a unit picked under one project cannot belong to another anyway.
    useEffect(() => {
        if (!project) return;
        units.invoke({ query: { project, limit: constants.maxPageSize } });
    }, [project]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => {
        const shared: Record<string, unknown> = {
            project: project || null,
            unit: unit || null,
            source: source || null,
            financing: financing || null,
            budgetMin,
            budgetMax,
            assignees: holders,
        };

        if (isEditing) return invoke({ method: "PATCH", data: shared });

        invoke({
            method: "POST",
            data: { ...shared, name: name.trim(), phone: (phone ?? "").replace(/\s/g, ""), email: email.trim() || null },
        });
    };

    const projectOptions = [
        { value: "", label: t.crm.general },
        ...(projects.data?.projects ?? []).map((item) => ({ value: item._id, label: readLangValue(item.name, lang) || t.crm.unnamed })),
    ];
    const unitOptions = [{ value: "", label: t.crm.notSet }, ...(units.data?.units ?? []).map((item) => ({ value: item._id, label: item.number }))];

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{isEditing ? t.crm.editLead : t.crm.addLead}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {!isEditing && (
                    <>
                        <TextInput value={name} onChange={setName} label={t.crm.fields.name} disabled={loading || isKnownPerson} />
                        {/* No caption: a flag and a fixed `+966` already say what the box is for. */}
                        <PhoneInput value={phone} onChange={setPhone} disabled={loading || isKnownPerson} />
                        <TextInput
                            value={email}
                            onChange={setEmail}
                            label={t.crm.fields.email}
                            type="email"
                            dir="ltr"
                            disabled={loading || isKnownPerson}
                        />
                    </>
                )}

                {/* Where they want it and where they came from — two short answers that read as
                    one line about the enquiry rather than two unrelated fields down the form. */}
                <div className="side-form__row side-form__row--pair">
                    <SelectInput value={project} onChange={setProject} options={projectOptions} label={t.crm.fields.project} disabled={loading} />
                    <SelectInput
                        value={source}
                        onChange={setSource}
                        options={constants.leadSources.map((item) => ({ value: item, label: t.crm.sources[item] }))}
                        label={t.crm.fields.source}
                        disabled={loading}
                    />
                </div>

                {project && (
                    <SelectInput
                        value={unit}
                        onChange={setUnit}
                        options={unitOptions}
                        label={t.crm.fields.unit}
                        disabled={loading || units.loading}
                    />
                )}

                {/* How they pay and how much are one question asked twice, so they are one group.
                    The range is joined into a single control: two full-width boxes stacked down the
                    form read as two unrelated fields, and people filled the first and left the second. */}
                <div className="side-form__group">
                    <span className="side-form__group-title">{t.crm.payMethod}</span>

                    <ChoiceInput
                        value={financing}
                        onChange={setFinancing}
                        options={constants.leadFinancing.map((item) => ({ value: item, label: t.crm.financing[item] }))}
                        disabled={loading}
                    />

                    <RangeInput
                        label={t.crm.budget}
                        fromLabel={t.crm.rangeFrom}
                        toLabel={t.crm.rangeTo}
                        from={budgetMin}
                        to={budgetMax}
                        onFrom={setBudgetMin}
                        onTo={setBudgetMax}
                        suffix={t.properties.currency}
                        disabled={loading}
                    />
                </div>

                <MultiSelectInput
                    value={holders}
                    onChange={setHolders}
                    options={(team.data?.members ?? []).map((member) => ({ value: member._id, label: member.name }))}
                    label={t.crm.fields.assignees}
                    disabled={loading}
                />

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
